import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { AlertTriangle, Check, Loader2, Clock, Euro, TrendingDown } from "lucide-react";
import { API } from "../../App";
import JobCardViewModal from "./JobCardViewModal";

/**
 * Admin-only "Job Alerts" panel — combines cost + duration anomaly alerts
 * into a single list, tagged by kind. Reads GET /api/stock/{cost,duration}-alerts
 * on mount and provides one-click acknowledge that POSTs to the matching
 * acknowledge endpoint and drops the row optimistically.
 *
 * Clicking anywhere on a row (outside the Acknowledge button) opens the
 * underlying Job Card in a read-only drilldown modal with an alert-context
 * banner and an inline Acknowledge action.
 */
const JobAlertsSection = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ackId, setAckId] = useState(null);
  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const [drilldownBooking, setDrilldownBooking] = useState(null);
  const [drilldownAlert, setDrilldownAlert] = useState(null);
  const [openingDrilldown, setOpeningDrilldown] = useState(null); // alert_id being fetched

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cost, dur, margin] = await Promise.all([
        axios.get(`${API}/stock/cost-alerts`, { params: { acknowledged: showAcknowledged } }),
        axios.get(`${API}/stock/duration-alerts`, { params: { acknowledged: showAcknowledged } }),
        axios.get(`${API}/stock/margin-alerts`, { params: { acknowledged: showAcknowledged } }),
      ]);
      const combined = [
        ...(cost.data?.alerts || []).map((a) => ({ ...a, kind: "cost" })),
        ...(dur.data?.alerts || []).map((a) => ({ ...a, kind: "duration" })),
        // Margin alerts share the same shape as the other two anomaly axes.
        // The measured `value` is `cost_ratio` (bigger = thinner margin),
        // so we display it as a percentage — never as a euro amount.
        ...(margin.data?.alerts || []).map((a) => ({ ...a, kind: "margin" })),
      ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setAlerts(combined);
    } catch (err) {
      toast.error("Failed to load job alerts");
    } finally {
      setLoading(false);
    }
  }, [showAcknowledged]);

  useEffect(() => {
    load();
  }, [load]);

  const acknowledge = async (alert) => {
    setAckId(alert.alert_id);
    // Path is derived from the alert kind so the three anomaly axes
    // stay symmetrical — add a fourth alert axis by mapping its kind
    // here, same shape as the existing branches.
    const path =
      alert.kind === "duration"
        ? "duration-alerts"
        : alert.kind === "margin"
        ? "margin-alerts"
        : "cost-alerts";
    try {
      await axios.post(`${API}/stock/${path}/${alert.alert_id}/acknowledge`);
      toast.success("Alert acknowledged");
      setAlerts((prev) =>
        showAcknowledged
          ? prev.map((a) =>
              a.alert_id === alert.alert_id
                ? { ...a, acknowledged: true, acknowledged_at: new Date().toISOString() }
                : a
            )
          : prev.filter((a) => a.alert_id !== alert.alert_id)
      );
      // Reflect ack state in the open drilldown, if any
      setDrilldownAlert((prev) =>
        prev && prev.alert_id === alert.alert_id ? { ...prev, acknowledged: true } : prev
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to acknowledge");
    } finally {
      setAckId(null);
    }
  };

  const openDrilldown = async (alert) => {
    if (!alert?.booking_id) {
      toast.error("This alert has no linked booking");
      return;
    }
    setOpeningDrilldown(alert.alert_id);
    try {
      const res = await axios.get(`${API}/bookings/${alert.booking_id}`);
      setDrilldownBooking(res.data);
      setDrilldownAlert(alert);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to open job card");
    } finally {
      setOpeningDrilldown(null);
    }
  };

  const closeDrilldown = () => {
    setDrilldownBooking(null);
    setDrilldownAlert(null);
  };

  return (
    <>
    <Card className="bg-slate-800/50 border-slate-700" data-testid="job-alerts-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Job Alerts
            </CardTitle>
            <CardDescription className="text-slate-400">
              Advisory flags when a completed job&apos;s material cost or duration is well outside the normal range for its service + vehicle size.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAcknowledged((s) => !s)}
            className="border-slate-600 text-slate-300"
            data-testid="toggle-acknowledged"
          >
            {showAcknowledged ? "Show unacknowledged only" : "Show all"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-8 text-slate-400" data-testid="job-alerts-empty">
            <div className="text-4xl mb-2">✓</div>
            <div className="text-white font-medium">No alerts right now</div>
            <div className="text-sm text-slate-500 mt-1">
              Nothing unusual has been flagged.
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="job-alerts-list">
            {alerts.map((a) => {
              const isDuration = a.kind === "duration";
              const isMargin = a.kind === "margin";
              // Formatter varies by axis:
              //   - duration → "min"
              //   - margin   → cost_ratio as a percentage (0.65 → 65%)
              //   - cost     → euros
              const fmt = (v) => {
                if (v === null || v === undefined) return "—";
                if (isDuration) return `${v} min`;
                if (isMargin) return `${(Number(v) * 100).toFixed(1)}%`;
                return `€${Number(v).toFixed(2)}`;
              };
              const isOpening = openingDrilldown === a.alert_id;
              // Colour + icon per kind — mirrors the existing cost /
              // duration palette (emerald / purple) and adds rose for
              // margin (bigger cost_ratio = worse, so warm tone).
              const iconColor = isDuration
                ? "text-purple-400"
                : isMargin
                ? "text-rose-400"
                : "text-emerald-400";
              const valueColor = isDuration
                ? "text-purple-300"
                : isMargin
                ? "text-rose-300"
                : "text-emerald-300";
              const KindIcon = isDuration ? Clock : isMargin ? TrendingDown : Euro;
              return (
                <div
                  key={a.alert_id}
                  className="grid grid-cols-12 gap-3 items-center bg-slate-900/50 rounded-lg p-3 hover:bg-slate-900/80 cursor-pointer transition-colors"
                  data-testid={`alert-row-${a.alert_id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => openDrilldown(a)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openDrilldown(a);
                    }
                  }}
                >
                  <div className="col-span-1">
                    {isOpening ? (
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    ) : (
                      <KindIcon className={`w-5 h-5 ${iconColor}`} />
                    )}
                  </div>
                  <div className="col-span-5 min-w-0">
                    <div className="text-white text-sm font-medium truncate">
                      {a.service_id} · {a.vehicle_size}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      Booking {a.booking_id || "—"}
                      {a.created_at ? ` · ${new Date(a.created_at).toLocaleString()}` : ""}
                    </div>
                  </div>
                  <div className="col-span-4 text-right text-sm">
                    <div className="text-white tabular-nums">
                      <span className={valueColor}>
                        {fmt(a.actual)}
                      </span>
                      <span className="text-slate-500"> vs median </span>
                      <span className="tabular-nums">{fmt(a.median)}</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      {a.multiplier}× threshold {fmt(a.threshold)} · n={a.sample_size}
                    </div>
                  </div>
                  <div
                    className="col-span-2 text-right"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    role="presentation"
                  >
                    {a.acknowledged ? (
                      <span
                        className="text-xs text-slate-500 inline-flex items-center gap-1"
                        data-testid={`alert-acknowledged-${a.alert_id}`}
                      >
                        <Check className="w-3 h-3" /> Ack&apos;d
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => acknowledge(a)}
                        disabled={ackId === a.alert_id}
                        className="bg-emerald-600 hover:bg-emerald-700 h-8"
                        data-testid={`alert-ack-${a.alert_id}`}
                      >
                        {ackId === a.alert_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Check className="w-3 h-3 mr-1" /> Acknowledge
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>

    <JobCardViewModal
      isAdmin
      isOpen={!!drilldownBooking}
      onClose={closeDrilldown}
      booking={drilldownBooking}
      alertContext={drilldownAlert}
      onAcknowledge={drilldownAlert && !drilldownAlert.acknowledged ? acknowledge : undefined}
    />
    </>
  );
};

export default JobAlertsSection;
